// src/lib/components/molecules/DateTimeDialogEditorMolecule.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { Button } from 'primereact/button';
import CustomDialogAtom from '../../atoms/CustomDialog/CustomDialogAtom'
import { DateTimePickerMolecule, DateTimeValue } from '../DateTimePicker/DateTimePickerMolecule';
import DateTimeDisplayAtom from '../../atoms/DateTimeDisplay/DateTimeDisplayAtom';
import moment from 'moment-timezone';
import './DateTimeDialogEditorMolecule.scss';

export interface DateTimeDialogEditorProps {
  /** ISO‑8601 Z string, e.g. '2025‑07‑24T04:00:00.000Z' */
  value: string | null;
  onChange: (iso: string | null) => void;
  onTimezoneChange?: (tz: string) => void;
  label?: string;
  dateFormat?: string;
  timezoneOptions?: string[];
  defaultTimezone?: string;
  disabled?: boolean;
  className?: string;
}

interface InternalValue {
  date: Date | null;
  time: string;      // "HH:mm"
  timezone: string;  // IANA TZ
}

const parseIso = (iso: string | null, defaultTz?: string): InternalValue => {
  const tz = defaultTz || moment.tz.guess();
  if (!iso) return { date: null, time: '00:00', timezone: tz };
  const m = moment.utc(iso).tz(tz);
  return {
    date: new Date(m.year(), m.month(), m.date()),
    time: m.format('HH:mm'),
    timezone: tz,
  };
};

const toIso = (v: InternalValue): string | null => {
  if (!v.date) return null;
  const ymd = moment(v.date).format('YYYY-MM-DD');
  return moment
    .tz(`${ymd} ${v.time}`, 'YYYY-MM-DD HH:mm', v.timezone)
    .toISOString();
};

const DateTimeDialogEditor: React.FC<DateTimeDialogEditorProps> = ({
  value,
  onChange,
  onTimezoneChange,
  label = 'Select Date & Time',
  dateFormat,
  timezoneOptions,
  defaultTimezone,
  disabled = false,
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [current, setCurrent] = useState<InternalValue>(() =>
    parseIso(value, defaultTimezone)
  );

  useEffect(() => {
    setCurrent(parseIso(value, defaultTimezone));
  }, [value, defaultTimezone]);

  const handleInternalChange = useCallback((next: DateTimeValue) => {
    setCurrent({ date: next.date, time: next.time, timezone: next.timezone });
  }, []);

  const handleSave = () => {
    const iso = toIso(current);
    onChange(iso);
    if (iso && onTimezoneChange) {
      onTimezoneChange(current.timezone);
    }
    setIsOpen(false);
  };

  return (
    <div className={`date-time-dialog-editor ${className}`}>
      <div className="date-time-dialog-editor__display">
        <DateTimeDisplayAtom
          date={current.date}
          time={current.time}
          timezone={current.timezone}
          format="compact"
          className="date-time-dialog-editor__value"
        />
        {!disabled && (
          <Button
            icon="pi pi-pencil"
            className="p-button-text p-button-sm date-time-dialog-editor__edit-btn"
            onClick={() => setIsOpen(true)}
            aria-label="Edit date and time"
          />
        )}
      </div>

      <CustomDialogAtom
        header={label}
        visible={isOpen}
        onHide={() => setIsOpen(false)}
        className="date-time-dialog-editor__dialog"
        style={{ width: 650 }}
        footer={
          <>
            <Button
              label="Cancel"
              icon="pi pi-times"
              className="p-button-text"
              onClick={() => setIsOpen(false)}
            />
            <Button
              label="Save"
              icon="pi pi-check"
              onClick={handleSave}
            />
          </>
        }
      >
        <DateTimePickerMolecule
          value={current}
          onChange={handleInternalChange}
          dateFormat={dateFormat}
          timezoneOptions={timezoneOptions}
          defaultTimezone={defaultTimezone}
          showPreview
        />
      </CustomDialogAtom>
    </div>
  );
};

export default DateTimeDialogEditor;
